/**
 * Week · by line view model — pure derivation.
 *
 * Seven days of the Line view side by side: lanes are the plant's work
 * centers, columns Monday..Sunday. Each cell is built with `buildLaneRows`
 * for that day, so a step that crosses midnight is a step in both day cells
 * (clipped to each day, cards carrying their "part n" labels), and the
 * empty crew lines are the same per-shift slots the day view offers. On
 * top of that: per-day coverage for the plant and week hours per lane.
 * Nothing here touches React or the server.
 */
import { addDays } from "date-fns";

import { LaborStandard } from "../../../../../services/labor-standards-api";
import { RosterAssignment } from "../../../../../services/roster-assignments-api";
import { ShiftInstance } from "../../../../../services/shifts-api";
import { ManufacturingOrder } from "../../../production-plan/utils/calculations";
import { localDay } from "../../../utils/time";
import { AvailabilityIndex } from "./availability";
import { buildLaneRows, LaneCard, LaneStep } from "./lane-rows"; // eslint-disable-line sort-imports

export interface WeekLaneDay {
	/** yyyy-MM-dd. */
	dayIso: string;
	date: Date;
	steps: LaneStep[];
	/** Cards from a step that is not one of the day's steps. */
	loose: LaneCard[];
	/** Crew lines filled / needed over the day's labor phases on this lane. */
	have: number;
	needed: number;
	/** Crew on this lane this day who are confirmed out (listed on their step, not counted in `have`). */
	outCount: number;
	/** Hours of assignments on this lane this day, clipped to the day. */
	hours: number;
}

export interface WeekLane {
	workcenter: string;
	/** Index 0 = Monday. */
	days: WeekLaneDay[];
	/** Sum of the lane's assignment hours over the week. */
	weekHours: number;
	/** No step and no card on any day — drawn as one thin row. */
	idle: boolean;
}

export interface WeekDayHeader {
	dayIso: string;
	date: Date;
	have: number;
	needed: number;
	/** Assigned workers confirmed out that day across the plant — the "· 1 out" on the day header. */
	outCount: number;
	/** Crew lines filled over crew lines needed across the plant, 0–100; null when nothing is needed. */
	coveragePct: number | null;
}

export interface WeekLanes {
	lanes: WeekLane[];
	/** Index 0 = Monday. */
	days: WeekDayHeader[];
}

const cardHours = (card: LaneCard): number => Math.max(0, card.end - card.start);

/**
 * The visible week by line. `weekStart` is the Monday. Work centers found
 * only in the data are appended after the plant's list, as in the day view.
 */
export function buildWeekLanes(
	orders: ManufacturingOrder[],
	assignments: RosterAssignment[],
	standards: LaborStandard[],
	workcenters: string[],
	weekStart: Date,
	options: { operationNames?: Map<number, string>; shifts?: ShiftInstance[]; availability?: AvailabilityIndex } = {}
): WeekLanes {
	const dates = Array.from({ length: 7 }, (_, d) => addDays(weekStart, d));
	const perDay = dates.map((day) =>
		buildLaneRows({
			day,
			workcenters,
			orders,
			standards,
			assignments,
			operationNames: options.operationNames,
			shifts: options.shifts,
			availability: options.availability,
		})
	);

	// Every lane any day produced, plant order first.
	const names: string[] = [];
	const seen = new Set<string>();
	[workcenters, ...perDay.map((rows) => rows.map((row) => row.workcenter))].flat().forEach((name) => {
		if (!seen.has(name)) {
			seen.add(name);
			names.push(name);
		}
	});

	const lanes: WeekLane[] = names.map((workcenter) => {
		const days: WeekLaneDay[] = dates.map((date, d) => {
			const row = perDay[d].find((r) => r.workcenter === workcenter);
			const steps = row?.steps ?? [];
			const loose = row?.loose ?? [];
			const have = steps.reduce((total, step) => total + step.crewHave, 0);
			const needed = steps.reduce((total, step) => total + step.crewNeeded, 0);
			const outCount =
				steps.reduce((total, step) => total + step.crewOut, 0) + new Set(loose.filter((card) => card.isOut).map((card) => card.workerId)).size;
			const hours =
				steps.reduce(
					(total, step) => total + step.phases.reduce((sum, phase) => sum + phase.cards.reduce((h, card) => h + cardHours(card), 0), 0),
					0
				) + loose.reduce((total, card) => total + cardHours(card), 0);
			return { dayIso: localDay(date), date, steps, loose, have, needed, outCount, hours };
		});
		return {
			workcenter,
			days,
			weekHours: days.reduce((total, day) => total + day.hours, 0),
			idle: days.every((day) => day.steps.length === 0 && day.loose.length === 0),
		};
	});

	const days: WeekDayHeader[] = dates.map((date, d) => {
		const have = lanes.reduce((total, lane) => total + lane.days[d].have, 0);
		const needed = lanes.reduce((total, lane) => total + lane.days[d].needed, 0);
		const outCount = lanes.reduce((total, lane) => total + lane.days[d].outCount, 0);
		return {
			dayIso: localDay(date),
			date,
			have,
			needed,
			outCount,
			coveragePct: needed > 0 ? Math.round((Math.min(have, needed) / needed) * 100) : null,
		};
	});

	return { lanes, days };
}

/**
 * The shift label a chip is grouped under: the shift's pattern name when the
 * card's shiftId is one of the loaded shifts, else by time of day — a block
 * starting before 14:00 is "1st", anything later "2nd".
 */
export function shiftLabelFor(shiftId: string | null | undefined, startHour: number, shifts: ShiftInstance[]): string {
	if (shiftId) {
		const shift = shifts.find((s) => s.shiftId === shiftId);
		if (shift?.shiftPatternName) {
			return shift.shiftPatternName;
		}
	}
	return startHour % 24 < 14 ? "1st" : "2nd";
}
